import { computed } from 'vue'
import type { ModeFrais, NatureFrais } from '@/features/domaine/types/opco'
import type { FraisDTO } from '@/features/api/extranet/dto/Demande/shared'
import {
  LibelleTypeMontantFrais,
  type FraisData,
  type FraisProps,
  type LigneFrais,
  type MetaFrais,
} from './Frais.types'
import { LABEL_MONTANT_DEMANDE_HT } from '../../language'
import { PropertyStatus } from '@/features/domaine/property-status'

export function useFraisFormation(props: FraisProps) {
  function getNatureFraisLib(nature: NatureFrais | null) {
    return props.natureFrais.find((item) => item.code === nature)?.intitule
  }

  const montantsTotauxParType = computed(() => {
    if (!props.fraisDataParMode) return {}
    return Object.fromEntries(
      Object.entries(props.fraisDataParMode).map(([type, typeData]) => {
        const total = typeData.frais.reduce((sum, frais) => {
          if (typeof frais.montant === 'number') {
            if (frais.baseCalcul === 'Quantite' && typeof frais.quantite === 'number') {
              return sum + frais.montant * frais.quantite
            } else {
              return sum + frais.montant
            }
          }
          return sum
        }, 0)
        return [type, total]
      }),
    )
  })

  function totalLabelForMode(modeFrais: ModeFrais) {
    const montant = montantsTotauxParType.value[modeFrais] || 0
    return `Montant Total HT :\u00A0\u00A0\u00A0\u00A0${montant} €`
  }

  const lignesFrais = computed<LigneFrais[]>(() => {
    if (!props.fraisDataParMode) return []

    const modes: ModeFrais[] = ['Pedagogique', 'Annexe']
    const result: LigneFrais[] = []

    for (const mode of modes) {
      const modeData: FraisData = props.fraisDataParMode[mode]
      if (!modeData || modeData.frais.length === 0) continue

      modeData.frais.forEach((frais: FraisDTO, index: number) => {
        const meta: MetaFrais = modeData.metaFrais![index]!
        result.push({
          type: 'frais',
          mode,
          fraisTitre: modeData.fraisTitre,
          fraisTitreHelp: modeData.fraisTitreHelp,
          frais,
          meta,
          isFirstOfMode: index === 0,
          hint: modeData.fraisHelp![frais.nature!],
        })
      })

      result.push({
        type: 'total',
        mode,
        totalLabel: totalLabelForMode(mode),
      })
    }

    return result
  })

  const getMontantFraisLib = (frais: FraisDTO) => {
    if (frais.baseCalcul === 'Montant') return LABEL_MONTANT_DEMANDE_HT
    return LibelleTypeMontantFrais[frais.nature!] ?? LABEL_MONTANT_DEMANDE_HT
  }

  const afficheQuantité = computed(() =>
    lignesFrais.value.some(
      (ligneFrais) =>
        ligneFrais.type === 'frais' &&
        ligneFrais.frais.baseCalcul === 'Quantite' &&
        ligneFrais.meta.montant?.$status !== PropertyStatus.NotPresent,
    ),
  )

  return {
    getMontantFraisLib,
    montantsTotauxParType,
    getNatureFraisLib,
    lignesFrais,
    totalLabelForMode,
    afficheQuantité,
  }
}
